/**
 * =============================================================================
 * FOOTER COMPONENT - Site-Wide Footer
 * =============================================================================
 * 
 * Displays at the bottom of every page via the Layout component.
 * Contains brand information, service links, and legal page links.
 * 
 * STRUCTURE:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  [Brand/Logo]        [Services]           [Information]        │
 * │  Description         - Search             - Terms  - Protection│
 * │                      - Sell               - Privacy - Contact  │
 * ├─────────────────────────────────────────────────────────────────┤
 * │                    © 2024 HittaDelar                            │
 * └─────────────────────────────────────────────────────────────────┘
 * 
 * EXTENDING:
 * To add new footer links:
 * 1. Services: Add to the "Tjänster" section
 * 2. Legal/Info: Add to the "Information" section
 * 3. Social media: Create a new section with social icons
 * 
 * @see src/components/Layout.tsx - Parent component
 * =============================================================================
 */

import { Link } from "react-router-dom";
import { usePlatform } from "@/hooks/usePlatform";
import { cn } from "@/lib/utils";
import hittadelarLogo from "@/assets/hittadelar-logo-main.png";

// =============================================================================
// FOOTER COMPONENT
// =============================================================================

export const Footer = () => {
  // Platform detection for iOS safe area handling
  const { isIOS, isMobile } = usePlatform();
  
  return (
    <footer className={cn(
      // Base styles
      "bg-secondary border-t border-border mt-auto",
      // iOS safe area padding (for home indicator)
      isIOS && isMobile && "ios-safe-area-bottom"
    )}>
      <div className="container mx-auto px-4 py-8">
        {/* =================================================================
            MAIN FOOTER GRID
            3 columns on desktop, stacked on mobile
        ================================================================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* ---------------------------------------------------------------
              BRAND SECTION
              Logo and site description
          --------------------------------------------------------------- */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={hittadelarLogo} alt="HittaDelar" className="h-6 w-6" />
              <span className="text-lg font-bold">HittaDelar</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Sveriges marknadsplats för begagnade bildelar
            </p>
            <h4 className="font-semibold mt-4 mb-2">Sociala medier</h4>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/hittadelar.se/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/share/17V7cSRd15/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#0066CC">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* ---------------------------------------------------------------
              SERVICES SECTION
              Main action links
          --------------------------------------------------------------- */}
          <div className="md:ml-8">
            <h3 className="font-semibold mb-3">Tjänster</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link 
                  to="/search" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Sök delar
                </Link>
              </li>
              <li>
                <Link 
                  to="/create-listing" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Sälj delar
                </Link>
              </li>
              {/* 
                ADD NEW SERVICE LINKS HERE
                Example:
                <li>
                  <Link to="/new-service" className="text-muted-foreground hover:text-primary transition-colors">
                    New Service
                  </Link>
                </li>
              */}
            </ul>
          </div>

          {/* ---------------------------------------------------------------
              INFORMATION SECTION
              Legal pages and contact
          --------------------------------------------------------------- */}
          <div className="md:-ml-24">
            <h3 className="font-semibold mb-3">Information</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Link 
                to="/terms-of-use" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Användarvillkor
              </Link>
              <Link 
                to="/fraktvillkor" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Fraktvillkor
              </Link>
              <Link 
                to="/buyer-protection" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Köpskyddsvillkor
              </Link>
              <Link 
                to="/privacy-policy" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Integritetspolicy
              </Link>
              <Link 
                to="/contact" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Kontakta oss
              </Link>
              <Link 
                to="/faq" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Vanliga frågor
              </Link>
              {/* 
                ADD NEW INFO LINKS HERE
                Example:
                <Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors">
                  FAQ
                </Link>
              */}
            </div>
          </div>
        </div>

        {/* =================================================================
            BOTTOM COPYRIGHT BAR
        ================================================================= */}
        <div className="border-t border-border mt-8 pt-6 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} HittaDelar. Alla rättigheter förbehållna.</p>
        </div>
      </div>
    </footer>
  );
};
